// src/services/paymentService.ts
import { supabase } from '../lib/supabase';
import {
    FunctionsHttpError
} from '@supabase/supabase-js'

interface CreateExpressAccountResponse { 
    accountId: string;
    onboardingUrl: string;
    status: string;
}

/**
 * Creates a Stripe Express account and returns the onboarding URL.
 * The createExpressAccount edge function handles both account creation and link generation.
 */
export async function setupExpressAccount(vendorId: string): Promise<string> {
    if (!vendorId) {
        throw new Error('vendorId is required to setup Express account');
    }

    const { data, error } = await supabase.functions.invoke<CreateExpressAccountResponse>(
        'createExpressAccount',
        {
            body: { vendorId }
        }
    );

    console.log('[paymentService] createExpressAccount response:', data, error);

    if (error) {
        console.error('[paymentService] createExpressAccount error:', error);
        
        if (error instanceof FunctionsHttpError) {
            try {
                const errBody = await error.context.json();
                console.error('createExpressAccount failed with:', errBody);
                throw new Error(errBody.error || error.message);
            } catch (parseError) {
                console.error('Failed to parse error response:', parseError);
                throw new Error(error.message || 'Failed to create Express account');
            }
        }
        throw error;
    }

    if (!data?.onboardingUrl) {
        throw new Error('No onboarding URL returned from createExpressAccount');
    }

    console.log('[paymentService] Express account created:', {
        accountId: data.accountId,
        status: data.status
    });

    return data.onboardingUrl;
}

/**
 * Creates a Stripe Express dashboard login link for a connected account.
 * This allows the seller to access their Express Dashboard to manage
 * bank accounts, view payouts, etc.
 */
export async function getExpressLoginLink(accountId: string): Promise<string> {
    if (!accountId) {
        throw new Error('accountId is required to get Express login link');
    }

    const { data, error } = await supabase.functions.invoke<{ url: string }>(
        'createLoginLink',
        {
            body: { accountId }
        }
    );

    console.log('[paymentService] createLoginLink response:', data, error);

    if (error) {
        console.error('[paymentService] createLoginLink error:', error);
        
        if (error instanceof FunctionsHttpError) {
            try {
                const errBody = await error.context.json();
                console.error('createLoginLink failed with:', errBody);
                throw new Error(errBody.error || error.message);
            } catch (parseError) {
                console.error('Failed to parse error response:', parseError);
                throw new Error(error.message || 'Failed to create login link');
            }
        }
        throw error;
    }

    if (!data?.url) {
        throw new Error('No login URL returned from createLoginLink');
    }

    return data.url;
}

/**
 * Calls your Edge Function to create a payment intent, optionally on behalf
 * of a connected account.
 */
export async function createPaymentIntent(
    amount: number,         // in cents
    currency: string,       // e.g. 'usd'
    connectedAccountId?: string
): Promise<string> {
    const { data, error } = await supabase.functions.invoke<{ clientSecret: string }>(
        'createPaymentIntent',
        {
            body: {
                amount,
                currency,
                connectedAccountId, // if you're using Connect
            },
        }
    );
    if (error || !data?.clientSecret) {
        console.error('createPaymentIntent error', error);
        throw error || new Error('No clientSecret returned');
    }
    return data.clientSecret;
}